import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { Installation } from '@shared/types/installation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, Clock, XCircle, ClipboardCheck } from 'lucide-react';

interface InstallationChecklistProps {
  installation: Installation;
}

// Group checklist items into logical categories with labels
const checklistGroups = {
  admin: {
    title: 'Administrative Tasks',
    description: 'Preparation and administrative tasks',
    items: [
      { key: 'checklistBookScaffolding', label: 'Book Scaffolding' },
      { key: 'checklistLetterScaffoldInstallDate', label: 'Letter - Scaffold/Install Date' },
      { key: 'checklistPvPlannerContractors', label: 'PV Planner - Contractors' },
      { key: 'checklistHiesCreated', label: 'HIES Created' },
      { key: 'checklistDepositInvoice', label: 'Deposit Invoice' },
    ],
  },
  onSite: {
    title: 'On-Site Tasks',
    description: 'Tasks completed during installation',
    items: [
      { key: 'checklistSiteVisitScheduled', label: 'Site Visit Scheduled' },
      { key: 'checklistMpanRequested', label: 'MPAN Requested' },
      { key: 'checklistExportMpanReceived', label: 'Export MPAN Received' },
      { key: 'checklistSwitchExportConnect', label: 'Switch on Export (CONNECT App)' },
      { key: 'checklistLaminateSchematic', label: 'Laminate Schematic (NC)' },
      { key: 'checklistLaminateStartupShutdown', label: 'Laminate Startup/Shutdown Procedures' },
      { key: 'checklistF17ContractManagement', label: 'F17 - Contract Management Sheet' },
      { key: 'checklistConsolidatedForm', label: 'Consolidated Form (F40A, F43A F44, F44A)' },
      { key: 'checklistDnoCommissioningSent', label: 'DNO Commissioning Sent' },
      { key: 'checklistEicCompleted', label: 'EIC - DP Completes On Site' },
      { key: 'checklistStructuralSurvey', label: 'Structural Survey' },
      { key: 'checklistShadeReport', label: 'Shade Report (Admin Complete)' },
      { key: 'checklistSolarPvDesignCalculations', label: 'Solar PV Design Calculations' },
    ],
  },
  postInstall: {
    title: 'Post-Installation',
    description: 'Tasks after installation is complete',
    items: [
      { key: 'checklistScaffoldRemoved', label: 'Scaffold Removed' },
      { key: 'checklistSendSunsynkApp', label: 'Send Sunsynk CONNECT App' },
      { key: 'checklistAmendmentsProposalInvoice', label: 'Amendments to Proposals + Invoice' },
      { key: 'checklistHiesReturnCompleted', label: 'HIES Return Completed' },
      { key: 'checklistPartPCertificateCreated', label: 'Part P Certificate DP EIC Created' },
      { key: 'checklistMcsCertification', label: 'MCS Certification' },
      { key: 'checklistFinancePhoenixPortal', label: 'Finance Taken - Complete on Phoenix Portal' },
      { key: 'checklistInverterSerialToStock', label: 'Inverter Serial Number Added to Stock' },
    ],
  },
  handover: {
    title: 'Handover Documents',
    description: 'Final documents and handover',
    items: [
      { key: 'checklistFinalPaymentReceived', label: 'Final Payment Received' },
      { key: 'checklistMcsCertificateProvided', label: 'MCS Certificate Provided' },
      { key: 'checklistEicCertificateProvided', label: 'EIC Certificate Provided' },
      { key: 'checklistPartPBuildingRegsCertificate', label: 'Part P/Building Regs Certificate' },
      { key: 'checklistOwnerManual', label: 'Owner Manual' },
      { key: 'checklistStructuralReport', label: 'Structural Report' },
      { key: 'checklistProposalUpdated', label: 'Proposal Updated (If Install Differed)' },
      { key: 'checklistSchematic', label: 'Schematic' },
      { key: 'checklistSolarPanelDetails', label: 'Solar Panel Product Details' },
      { key: 'checklistSolarPvFormsConsolidated', label: 'Solar PV Forms Consolidated' },
      { key: 'checklistInverterWarranty', label: 'Inverter Extended Warranty' },
      { key: 'checklistFinalInvoice', label: 'Final Invoice(s)' },
      { key: 'checklistCompleteOpenSolar', label: 'Complete Job in Open Solar' },
    ],
  },
  maintaining: {
    title: 'Maintaining',
    description: 'Ongoing maintenance and follow-up',
    items: [
      { key: 'checklistCustomerSatisfactionSurvey', label: 'Customer Satisfaction Survey' },
      { key: 'checklistDiariseYearCheck', label: 'Diarise 1 Year Check' },
      { key: 'checklistAddSerialNumbersStock', label: 'Add Serial Numbers to Stock Report' },
    ],
  },
};

// Fixed Status badge component that properly handles DOM nesting
const StatusBadge = ({ status }: { status: string }) => {
  // Define badge properties based on status
  let variant: 'default' | 'secondary' | 'success' | 'destructive' | 'outline' = 'outline';
  let text = 'Pending';
  let icon = <Clock className="h-3 w-3" />;

  // Set appropriate properties based on status
  switch (status) {
    case 'completed':
      variant = 'success';
      text = 'Completed';
      icon = <CheckCircle className="h-3 w-3" />;
      break;
    case 'failed':
      variant = 'destructive';
      text = 'Failed';
      icon = <XCircle className="h-3 w-3" />;
      break;
    case 'in_progress':
      variant = 'secondary';
      text = 'In Progress';
      icon = <Clock className="h-3 w-3" />;
      break;
    default:
      variant = 'outline';
      text = 'Pending';
      icon = <Clock className="h-3 w-3" />;
      break;
  }

  // Return badge with a div wrapper to avoid DOM nesting issues
  return (
    <div className="inline-block">
      <Badge variant={variant} className="flex items-center gap-1">
        {icon} {text}
      </Badge>
    </div>
  );
};

export function InstallationChecklist({ installation }: InstallationChecklistProps) {
  // Get the correct installation object from the unusual data structure
  // The data is coming as an object with a "0" property containing the actual installation
  const installationData = installation && installation[0] ? installation[0] : installation;

  // Debug logging
  console.log('Installation object received in InstallationChecklist:', installation);
  console.log('Processing installationData:', installationData);
  console.log('Installation ID type:', typeof installationData?.id);
  console.log('Installation ID value:', installationData?.id);

  const [activeTab, setActiveTab] = useState<string>('admin');
  const { toast } = useToast();

  // Calculate progress percentages for each category and overall
  const calculateProgress = (categoryKey: string) => {
    const items = checklistGroups[categoryKey as keyof typeof checklistGroups].items;
    if (!items || !items.length) return 0;

    const completedCount = items.filter(
      (item) => installationData && installationData[item.key as keyof Installation] === 'completed'
    ).length;

    return Math.round((completedCount / items.length) * 100) || 0; // Return 0 if NaN
  };

  const calculateOverallProgress = () => {
    let totalItems = 0;
    let completedItems = 0;
    let failedItems = 0;
    let inProgressItems = 0;

    // Ensure installation exists and has properties
    if (!installationData) {
      return {
        percentage: 0,
        completedCount: 0,
        failedCount: 0,
        inProgressCount: 0,
        totalCount: 0,
      };
    }

    Object.keys(checklistGroups).forEach((categoryKey) => {
      const group = checklistGroups[categoryKey as keyof typeof checklistGroups];
      const items = group ? group.items : [];

      if (items && items.length) {
        totalItems += items.length;

        items.forEach((item) => {
          if (!item || !item.key) return;

          const status = installationData[item.key as keyof Installation] as string;
          if (status === 'completed') {
            completedItems++;
          } else if (status === 'failed') {
            failedItems++;
          } else if (status === 'in_progress') {
            inProgressItems++;
          }
        });
      }
    });

    // Calculate percentage with safeguards against NaN
    const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    return {
      percentage: isNaN(percentage) ? 0 : percentage,
      completedCount: completedItems || 0,
      failedCount: failedItems || 0,
      inProgressCount: inProgressItems || 0,
      totalCount: totalItems || 0,
    };
  };

  // Determine installation status based on checklist progress
  const determineInstallationStatus = () => {
    const progress = calculateOverallProgress();

    // If there are any failed items, set status to "needs_attention"
    if (progress.failedCount > 0) {
      return 'needs_attention';
    }

    // If everything is completed, set status to "completed"
    if (progress.percentage === 100) {
      return 'completed';
    }

    // If more than 0% but less than 100% completed, or any items in progress, set status to "in_progress"
    if (progress.percentage > 0 || progress.inProgressCount > 0) {
      return 'in_progress';
    }

    // If we have a scheduled date but no progress, use scheduled status
    if (installationData.scheduledDate && progress.percentage === 0) {
      return 'scheduled';
    }

    // Default to pending for all other cases
    return 'pending';
  };

  // Update installation status based on checklist progress - completely rewritten for reliability
  const updateInstallationStatus = useMutation({
    mutationFn: async (newStatus: string) => {
      // Validate inputs
      if (!newStatus || typeof newStatus !== 'string') {
        throw new Error('Invalid status value');
      }

      // Ensure we have a valid installation ID with safer parsing
      if (!installationData) {
        throw new Error('Installation object is missing');
      }

      // Ensure installation ID is valid and correctly parsed - handle both string and number types
      const installationId =
        typeof installationData.id === 'number'
          ? installationData.id
          : parseInt(String(installationData.id), 10);

      if (isNaN(installationId) || installationId <= 0) {
        throw new Error(`Invalid installation ID: ${installationData.id}`);
      }

      console.log(`Updating installation status to ${newStatus} for ID: ${installationId}`);

      // Use the object form of apiRequest for greater reliability
      return apiRequest({
        method: 'PATCH',
        url: `/api/installations/${installationId}`,
        data: { status: newStatus },
      });
    },
    onSuccess: () => {
      try {
        // Ensure we're using the right installation ID format for cache invalidation
        const installationId =
          typeof installationData.id === 'number'
            ? installationData.id
            : parseInt(String(installationData.id), 10);

        queryClient.invalidateQueries({ queryKey: [`/api/installations/${installationId}`] });
        queryClient.invalidateQueries({ queryKey: ['/api/installations'] });

        toast({
          title: 'Status updated',
          description: `Installation status has been updated.`,
        });
      } catch (err) {
        console.error('Error in onSuccess handler:', err);
      }
    },
    onError: (error: Error) => {
      console.error('Failed to update installation status:', error);
      toast({
        title: 'Status update failed',
        description: `Failed to update installation status: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    },
  });

  // Update checklist item mutation with improved reliability
  const updateChecklistItem = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      // Validate inputs before sending to server
      if (!key || typeof key !== 'string') {
        throw new Error('Invalid checklist item key');
      }

      if (!value || typeof value !== 'string') {
        throw new Error('Invalid status value');
      }

      // Ensure we have a valid installation ID (using safer number parsing)
      if (!installationData) {
        throw new Error('Installation object is missing');
      }

      // Ensure installation ID is valid and correctly parsed - handle both string and number types
      const installationId =
        typeof installationData.id === 'number'
          ? installationData.id
          : parseInt(String(installationData.id), 10);

      if (isNaN(installationId) || installationId <= 0) {
        throw new Error(`Invalid installation ID: ${installationData.id}`);
      }

      // Make the API request with validated data and better error handling
      // Use the object form of apiRequest for greater reliability (matching the API's expected format)
      try {
        console.log(
          `Making API request to update checklist item ${key} to ${value} for installation ${installationId}`
        );

        const response = await apiRequest({
          method: 'PATCH',
          url: `/api/installations/${installationId}`,
          data: { [key]: value },
        });

        if (!response) {
          throw new Error('No response received from server');
        }

        return response;
      } catch (error) {
        console.error('API request error:', error);
        throw new Error(error instanceof Error ? error.message : 'Failed to update checklist item');
      }
    },
    onSuccess: (data, variables) => {
      // Log the updated information for debugging
      console.log(`Checklist item ${variables.key} updated successfully to ${variables.value}`);

      try {
        // Ensure we're using the right installation ID format for cache invalidation
        const installationId =
          typeof installationData.id === 'number'
            ? installationData.id
            : parseInt(String(installationData.id), 10);

        // Refresh the installation data with correctly formatted ID
        queryClient.invalidateQueries({ queryKey: [`/api/installations/${installationId}`] });
        queryClient.invalidateQueries({ queryKey: ['/api/installations'] });

        // When a checklist item is updated, determine and update the installation status
        const newStatus = determineInstallationStatus();
        if (newStatus !== installationData.status) {
          console.log(
            `Updating installation status from ${installationData.status} to ${newStatus}`
          );
          updateInstallationStatus.mutate(newStatus);
        }

        toast({
          title: 'Checklist updated',
          description: `Item "${variables.key.replace('checklist', '')}" status changed to "${variables.value.replace('_', ' ')}"`,
        });
      } catch (err) {
        console.error('Error in onSuccess handler:', err);
      }
    },
    onError: (error: Error, variables) => {
      console.error(`Failed to update checklist item ${variables.key}:`, error);
      toast({
        title: 'Update failed',
        description: `Failed to update checklist: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    },
  });

  // Handle status change with improved error handling and detailed logging
  const handleStatusChange = (key: string, newStatus: string) => {
    if (updateChecklistItem.isPending) return; // Prevent multiple clicks

    try {
      // Validate the key and status to make sure they're valid
      if (!key || !newStatus) {
        toast({
          title: 'Invalid update',
          description: 'Cannot update with empty key or status',
          variant: 'destructive',
        });
        return;
      }

      // Make sure installation is defined and has an ID
      if (!installationData) {
        console.error('Installation object is undefined');
        toast({
          title: 'Installation data missing',
          description: 'Cannot update checklist: installation data is missing',
          variant: 'destructive',
        });
        return;
      }

      // Explicitly ensure we're handling a number type for the installation ID
      const installationId =
        typeof installationData.id === 'number'
          ? installationData.id
          : parseInt(String(installationData.id), 10);

      // Verify ID is valid
      if (isNaN(installationId) || installationId <= 0) {
        console.error(`Invalid installation ID: ${installationData.id}`, installationData);
        toast({
          title: 'Installation ID invalid',
          description: `Cannot update installation with ID: ${installationData.id}`,
          variant: 'destructive',
        });
        return;
      }

      // Log the validated ID for debugging
      console.log(
        `Using validated installation ID: ${installationId} for updating ${key} to ${newStatus}`
      );

      // Send the update with proper validation
      updateChecklistItem.mutate({ key, value: newStatus });
    } catch (err) {
      console.error('Error in handleStatusChange:', err);
      toast({
        title: 'Update error',
        description: 'An unexpected error occurred while updating the checklist',
        variant: 'destructive',
      });
    }
  };

  // Calculate overall progress
  const overallProgress = calculateOverallProgress();
  // Determine current installation status
  const currentStatus = determineInstallationStatus();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          Installation Checklist
        </CardTitle>
        <CardDescription>Track the progress of installation tasks</CardDescription>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm font-medium">{overallProgress.percentage.toString()}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5 mb-3">
            <div
              className="bg-primary h-2.5 rounded-full"
              style={{ width: `${overallProgress.percentage}%` }}
            ></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            <div className="bg-muted rounded p-2 text-center">
              <div className="text-lg font-medium">{overallProgress.completedCount.toString()}</div>
              <div className="text-xs text-muted-foreground">Completed</div>
            </div>
            <div className="bg-muted rounded p-2 text-center">
              <div className="text-lg font-medium">
                {overallProgress.inProgressCount.toString()}
              </div>
              <div className="text-xs text-muted-foreground">In Progress</div>
            </div>
            <div className="bg-muted rounded p-2 text-center">
              <div className="text-lg font-medium">{overallProgress.failedCount.toString()}</div>
              <div className="text-xs text-muted-foreground">Failed</div>
            </div>
            <div className="bg-muted rounded p-2 text-center">
              <div className="text-lg font-medium">{overallProgress.totalCount.toString()}</div>
              <div className="text-xs text-muted-foreground">Total Items</div>
            </div>
          </div>

          {currentStatus !== installationData.status && (
            <div className="mt-3 p-2 border rounded-md bg-muted/50">
              <p className="text-sm text-muted-foreground">
                Based on checklist progress, installation status will be updated to "{currentStatus}
                ".
              </p>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs
          defaultValue={activeTab}
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.entries(checklistGroups).map(([key, group]) => (
              <TabsTrigger key={key} value={key} className="relative">
                {group.title}
                <Badge variant="secondary" className="absolute -top-2 -right-2 text-xs">
                  {calculateProgress(key)}%
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(checklistGroups).map(([key, group]) => (
            <TabsContent key={key} value={key}>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">{group.title}</h3>
                  <p className="text-sm text-muted-foreground">{group.description}</p>
                </div>

                <div className="grid gap-4">
                  {group.items.map((item) => {
                    const status =
                      (installationData[item.key as keyof Installation] as string) || 'pending';

                    return (
                      <div
                        key={item.key}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-card"
                      >
                        <div className="mb-2 sm:mb-0 flex-1">
                          <h4 className="font-medium">{item.label}</h4>
                        </div>

                        <div className="flex gap-2 items-center">
                          <div
                            className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors ${status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-green-50'}`}
                            onClick={() => handleStatusChange(item.key, 'completed')}
                            title="Mark as completed"
                          >
                            <CheckCircle
                              className={`h-5 w-5 ${status === 'completed' ? 'opacity-100' : 'opacity-70'}`}
                            />
                          </div>

                          <div
                            className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors ${status === 'in_progress' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 hover:bg-blue-50'}`}
                            onClick={() => handleStatusChange(item.key, 'in_progress')}
                            title="Mark as in progress"
                          >
                            <Clock
                              className={`h-5 w-5 ${status === 'in_progress' ? 'opacity-100' : 'opacity-70'}`}
                            />
                          </div>

                          <div
                            className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition-colors ${status === 'failed' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400 hover:bg-red-50'}`}
                            onClick={() => handleStatusChange(item.key, 'failed')}
                            title="Mark as failed"
                          >
                            <XCircle
                              className={`h-5 w-5 ${status === 'failed' ? 'opacity-100' : 'opacity-70'}`}
                            />
                          </div>

                          <StatusBadge status={status} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
